import React, { MouseEvent, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Alert, IconButton, CircularProgress, Button, Stack } from '@mui/material';
import GridEmpty from '../components/GridEmpty.tsx';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import EditTenantDialog from '../components/EditTenantDialog.tsx';
import Chip from '@mui/material/Chip';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTenants } from '../hooks/useTenants.ts';
import { deleteTenant } from '../services/apiService.ts';
import { useSnackbar } from 'notistack';
import InviteDialog from '../components/InviteDialog.tsx';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog.tsx';
import CreateTenantDialog from '../components/CreateTenantDialog.tsx';
import type { TenantRow } from '../hooks/useTenants.ts';

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format('DD MMM YYYY, HH:mm') : 'Not recorded';

const formatLabel = (value?: string | null, fallback = 'Unknown') => {
  if (!value) {
    return fallback;
  }
  if (value === 'UNMAPPED') {
    return 'Needs mapping';
  }
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const TenantsListPage: React.FC = () => {
  const { rows, loading, error, refetch } = useTenants();
  const [dialogTenant, setDialogTenant] = useState<number | null>(null);
  const [deleteTenantId, setDeleteTenantId] = useState<number | null>(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [editTenant,setEditTenant]=useState<any|null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const stopRowNavigation = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  const renderCatalogChip = (row: TenantRow) => {
    const label = row.catalogPlanCode || 'MANUAL';
    if (label === 'UNMAPPED') {
      return <Chip label="Needs mapping" color="warning" size="small" />;
    }
    return (
      <Chip
        label={formatLabel(label, 'Manual')}
        color={row.catalogPlanMapped ? 'primary' : 'default'}
        size="small"
        variant={row.catalogPlanMapped ? 'filled' : 'outlined'}
      />
    );
  };

  const renderReadinessChip = (row: TenantRow) => {
    const blockers = row.paidPilotBlockers || [];
    if (blockers.length > 0) {
      return <Chip label={`${blockers.length} blocker${blockers.length > 1 ? 's' : ''}`} color="warning" size="small" />;
    }
    if (row.checkoutReady) {
      return <Chip label="Checkout ready" color="success" size="small" />;
    }
    if (row.checkoutReady === false) {
      return <Chip label="Blocked" color="warning" size="small" variant="outlined" />;
    }
    return <Chip label="Unknown" color="default" size="small" variant="outlined" />;
  };

  const cols: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'enabledModules',
      headerName: 'Modules',
      flex: 1,
      valueGetter: (params) => Array.isArray(params.row.enabledModules) ? params.row.enabledModules.join(', ') : (params.row.enabledModules || '')
    },
    { field: 'status', headerName: 'Status', width: 130, renderCell: params=> (
      <Chip label={formatLabel(params.value)} color={params.value==='ACTIVE'?'success':'default'} size="small" />
    ) },
    { field: 'billingState', headerName: 'Billing', width: 130, renderCell: params=> (
      <Chip label={formatLabel(params.value, 'Pilot')} color={params.value==='ACTIVE'?'success':params.value==='TRIAL'?'info':params.value==='SUSPENDED'?'warning':'default'} size="small" />
    ) },
    {
      field: 'catalogPlanCode',
      headerName: 'Catalog',
      width: 140,
      renderCell: params => renderCatalogChip(params.row),
    },
    {
      field: 'plan',
      headerName: 'Plan',
      width: 130,
      renderCell: params => <Chip label={formatLabel(params.value, 'Not set')} size="small" variant="outlined" />,
    },
    {
      field: 'checkoutReady',
      headerName: 'Readiness',
      width: 150,
      renderCell: params => {
        return renderReadinessChip(params.row as TenantRow);
      }
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 180,
      valueFormatter: (params) => formatDateTime(params.value),
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      renderCell: params => (
        <>
          <IconButton
            size="small"
            color="primary"
            onClick={(event) => {
              stopRowNavigation(event);
              setEditTenant(params.row);
            }}
          >
            <EditIcon fontSize="small"/>
          </IconButton>
          <IconButton
            size="small"
            color="secondary"
            onClick={(event) => {
              stopRowNavigation(event);
              setDialogTenant(params.row.id);
            }}
          >
            <PersonAddIcon fontSize="small"/>
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={(event) => {
              stopRowNavigation(event);
              setDeleteTenantId(params.row.id);
            }}
          >
            <DeleteIcon fontSize="small"/>
          </IconButton>
        </>
      )
    }
  ];

  return (
    <>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Nu am putut incarca lista de tenanti. Reincearca dupa ce revine API-ul.
        </Alert>
      ) : null}
      <Stack direction="row" justifyContent="flex-end" mb={1}>
        <Button variant="contained" onClick={() => setOpenCreate(true)}>Add Tenant</Button>
      </Stack>
      <div style={{ height: 600, width: '100%' }}>
      {loading ? <CircularProgress /> : (
        <DataGrid
          rows={rows}
          columns={cols}
          autoHeight
          pageSizeOptions={[10,20]}
          components={{ NoRowsOverlay: GridEmpty }}
          onRowClick={(params)=>{
            const id = params.row.id ?? params.id;
            if (!id) return;
            localStorage.setItem('saSelectedTenant', String(id));
            navigate(`/dashboard/tenants/${id}`);
          }}
        />
      )}

      {dialogTenant && (
        <InviteDialog
          tenantId={dialogTenant}
          open={true}
          onClose={() => setDialogTenant(null)}
          onSuccess={() => {
            enqueueSnackbar('Invitation created', { variant: 'success' });
          }}
        />
      )}

      {/* Confirm delete dialog */}
      {deleteTenantId && (
        <ConfirmDeleteDialog
          open={true}
          onClose={() => setDeleteTenantId(null)}
          onConfirm={async () => {
            try {
              await deleteTenant(String(deleteTenantId));
              enqueueSnackbar('Tenant deleted', { variant: 'success' });
              setDeleteTenantId(null);
              refetch();
            } catch (e: any) {
              const friendly = e.response?.data?.message || e.response?.data?.error || e.message;
              enqueueSnackbar(friendly, { variant: 'error' });
            }
          }}
        />
      )}
      {editTenant && (
        <EditTenantDialog
          tenant={editTenant}
          open={!!editTenant}
          onClose={()=>setEditTenant(null)}
          onSaved={refetch}
        />
      )}

      {openCreate && (
        <CreateTenantDialog
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          onSuccess={refetch}
        />
      )}
      </div>
    </>
  );
};

export default TenantsListPage; 
